import React, { act, useEffect, useState } from "react"
import TextInput from "../../components/commons/TextInput"
import TextAreaInput from "../../components/commons/TextAreaInput"
import Dropdown from "../../components/commons/Dropdown"
import UploadInput from "../../components/commons/UploadInput"
import { getAllCategories, getCategoryById } from "../../firebase/categories"
import { getActiveBoxTypes } from "../../firebase/boxTypes"
import { getActiveConditions } from "../../firebase/conditions"
import { toast } from "react-toastify"
import { createListing } from "../../firebase/listings"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/authContext"
import { useAtom } from "jotai"
import { userAtom } from "../../store"
import { brands, rarities, boxTypes as staticBoxTypes } from "../../utils/data"
import DialogConfirmation from "../../components/dialogs/DialogConfirmation"

const MarketplaceCreateListing = () => {
	const [categories, setCategories] = useState([])
	const [subCategories, setSubCategories] = useState([])
	const [boxTypes, setBoxTypes] = useState([])
	const [conditions, setConditions] = useState([])
	const [title, setTitle] = useState("")
	const [brand, setBrand] = useState(brands[0].value)
	const [condition, setCondition] = useState("")
	const [rarity, setRarity] = useState(rarities[0].value)
	const [boxType, setBoxType] = useState("")
	const [price, setPrice] = useState(null)
	const [originalPrice, setOriginalPrice] = useState(null)
	const [pictures, setPictures] = useState({ 0: null, 1: null, 2: null, 3: null, 4: null })
	const [category, setCategory] = useState("")
	const [subCategory, setSubCategory] = useState("")
	const [description, setDescription] = useState("")
	const [images, setImages] = useState([])
	const { user } = useAuth()
	const [userData, setUserData] = useAtom(userAtom)
	const [activeCreate, setActiveCreate] = useState(false)
	const [quantity, setQuantity] = useState(1)
	const [releasedYear, setReleasedYear] = useState(2019)
	const [upcCodes, setUpcCodes] = useState("")
	const [sellerSKU, setSellerSKU] = useState("")
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const navigate = useNavigate()
	const paymentOptions = [
		{ label: "Card (Visa, Mastercard, Apple Pay, Google Pay)", value: "Card" },
		{ label: "PayPal", value: "PayPal" },
		{ label: "ACH Bank Debit", value: "ACH Bank Debit" },
		{ label: "Bank Wire (SWIFT)", value: "Bank Wire" },
	]
	const [paymentMethods, setPaymentMethods] = useState([])

	useEffect(() => {
		getAllCategories().then((cats) => {
			const catOptions = []

			cats.forEach((cat) => {
				catOptions.push({ value: cat.id, label: cat.name.toUpperCase() })
			})

			if (cats.length > 0)
				setCategory(cats[0].id)

			setCategories(catOptions)
		})

		// Fetch active box types
		getActiveBoxTypes().then((activeBoxTypes) => {
			console.log("Fetched box types:", activeBoxTypes); // Debug log
			
			let boxTypeOptions = [];
			
			if (activeBoxTypes.length > 0) {
				// Use Firebase data
				boxTypeOptions = activeBoxTypes.map(bt => ({
					value: bt.value,
					label: bt.label
				}))
			} else {
				// Fallback to static data
				console.log("No active box types found, using static data");
				boxTypeOptions = staticBoxTypes;
			}
			
			setBoxTypes(boxTypeOptions)
			
			// Set default box type if available
			// if (boxTypeOptions.length > 0) {
			// 	setBoxType(boxTypeOptions[0].value)
			// }
		}).catch(error => {
			console.error("Error fetching box types:", error); // Debug log
			// Fallback to static data on error
			setBoxTypes(staticBoxTypes)
			setBoxType(staticBoxTypes[0].value)
		})

		// Fetch active conditions
		getActiveConditions().then((activeConditions) => {
			console.log("Fetched conditions:", activeConditions); // Debug log
			
			const conditionOptions = activeConditions.map(cond => ({
				value: cond.label,
				label: cond.label
			}))
			
			setConditions(conditionOptions)
			
			// Set default condition if available
			if (conditionOptions.length > 0) {
				setCondition(conditionOptions[0].value)
			}
		}).catch(error => {
			console.error("Error fetching conditions:", error); // Debug log
		})
	}, [])

	useEffect(() => {
		getCategoryById(category).then((data) => {
			if (data && data.subCategories) {
				setSubCategories(generateSubCategoryOptions(data))
			}
		})
	}, [category])

	useEffect(() => {
		let flag = true
		if (pictures.length === 0) flag = false
		if (!title || title.length === 0) flag = false
		if (!price || price < 1) flag = false
		if (!description || description.length === 0) flag = false
		if (!releasedYear || releasedYear < 1) flag = false
		if (!quantity || quantity < 1) flag = false
		if (paymentMethods.length === 0) flag = false
		setActiveCreate(flag)
	}, [pictures, title, price, description, releasedYear, quantity, paymentMethods])

	const generateSubCategoryOptions = (item) => {
		const catOptions = []

		item.subCategories.forEach((cat) => {
			catOptions.push({ value: cat, label: cat.toUpperCase() })
		})

		return catOptions
	}

	const onCreateListing = async () => {
		const newPictures = []
		for (let i = 0; i < 5; i++) {
			if (pictures[5 - i] !== null) newPictures.push(pictures[i])
		}

		await createListing({ userId: user.uid, username: userData.username, pictures: newPictures, title, condition, price, category, subCategory, description, brand, releasedYear, boxType, upcCodes, sellerSKU, quantity, originalPrice, paymentMethods })
		setShowConfirmModal(true)
	}

	const handleCloseConfirmModal = () => {
		setShowConfirmModal(false)
		navigate("/my-listings")
	}

	const addImage = (file, index) => {
		let temp = { ...pictures, [index]: file }
		setPictures({ ...temp })
	}

	const handleFileRemove = (index) => {
		let temp = { ...pictures, [index]: null }
		setPictures({ ...temp })
	}

	const handleUpcCodesChange = (e) => {
		setUpcCodes(e.currentTarget.value)
	}

	const handleSellerSKUChange = (e) => {
		setSellerSKU(e.currentTarget.value)
	}

	const handleReleaseYearChange = (e) => {
		if (parseInt(e.currentTarget.value) < 1990) {
			setReleasedYear(1990)
			return;
		}

		if (parseInt(e.currentTarget.value) > 2025) {
			setReleasedYear(2025)
			return;
		}

		setReleasedYear(parseInt(e.currentTarget.value))
	}
	return (
		<div>
			<DialogConfirmation
				message="Your listing has been successfully uploaded! Please allow up to 24 hours for approval."
				open={showConfirmModal}
				onClose={handleCloseConfirmModal}
				buttonText="Done"
				title="Listing Created"
				iconSrc="/assets/icons/icon-check-circle.svg"
				type="confirm"
			/>

			<h1 className="mt-5 font-aero uppercase text-white leading-[1.2] text-[28px] lg:text-[32px]">
				create Listing
			</h1>

			<hr className="my-10 border-gray"></hr>

			<div className="w-full lg:flex justify-between gap-6">
				<span className="block uppercase text-primary text-[12px] max-lg:mb-8">Upload Imgs</span>

				<div>
					<div className="flex mb-8 max-lg:overflow-x-auto -mt-3 pt-3 x-scrollbar gap-x-5">
						{[0, 1, 2, 3, 4].map((item) =>
							<UploadInput
								className="w-[150px] min-w-[150px] object-cover h-[150px]"
								uploadId={`create-listing-${item}`}
								onFileSelect={(file) => addImage(file, item)}
								onFileRemove={handleFileRemove}
								id={item}
							/>
						)}
					</div>

					<div className="w-fit">
						{/* <label htmlFor="image-upload" className="text-white font-thin text-xs"> */}
						<div className="flex items-center py-3 text-gray-100 text-xs">
							<img src="/assets/icons/icon-upload.svg" alt="icon-upload" className="mr-2" />
							PNG, JPG Up to 5 imgs
						</div>
						{/* </label> */}
					</div>
				</div>
			</div>

			<hr className="my-10 border-gray"></hr>

			<div className="lg:flex justify-between gap-6">
				<span className="block uppercase text-primary text-[12px] max-lg:mb-8">Details</span>

				<div>
					<div className="grid sm:grid-cols-2 gap-x-6 gap-y-7 text-white lg:w-[830px]">
						<label className="w-full  max-sm:col-span-2">
							<span className="text-sm mb-3 block">
								Category
							</span>
							<Dropdown
								options={categories}
								placeholder="Select Category"
								onChange={(e) => setCategory(e.target.value)}
								className="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						<label className="w-full  max-sm:col-span-2">
							<span className="text-sm mb-3 block">
								Sub-Category
							</span>
							<Dropdown
								options={subCategories}
								placeholder="--- None ---"
								onChange={(e) => setSubCategory(e.target.value)}
								allowClear
								className="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						<label className="w-full  col-span-2">
							<span className="text-sm mb-3 block">Title</span>
							<TextInput
								placeholder="Enter your title here"
								inputClassName="placeholder:text-white/60"
								value={title}
								onChange={(value) => setTitle(value)}
								divClassName="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						<label className="w-full col-span-1  max-sm:col-span-2">
							<span className="text-sm mb-3 block">Price</span>
							<div className="relative border-[1px] border-gray  rounded-[6px]">
								<input
									placeholder="100"
									// inputClassName="placeholder:text-white/60"
									defaultType="number"
									value={price}
									type="number"
									onChange={(e) => setPrice(e.currentTarget.value)}
									className=" placeholder:text-white/60 bg-transparent text-white p-4 px-[30px] w-full outline-none focus:outline-none placeholder:text-white resize-none text-xs"
									onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
								/>

								<div className="absolute left-5 top-[50%] -translate-y-1/2 -mt-[1px] text-white text-xs">$</div>
							</div>
						</label>

						<label className="w-full col-span-1  max-sm:col-span-2">
							<div className="flex items-center justify-between">
								<span className="text-sm mb-3 block">Your Cost</span>
								<span className="text-xs mb-3 block text-[#777] max-lg:hidden">Won't Show On Listings*</span>
							</div>
							<div className="relative border-[1px] border-gray  rounded-[6px]">
								<input
									placeholder="100"
									// inputClassName="placeholder:text-white/60"
									defaultType="number"
									value={originalPrice}
									type="number"
									onChange={(e) => setOriginalPrice(e.currentTarget.value)}
									className=" placeholder:text-white/60 bg-transparent text-white p-4 px-[30px] w-full outline-none focus:outline-none placeholder:text-white resize-none text-xs"
									onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
								/>

								<div className="absolute left-5 top-[50%] -translate-y-1/2 -mt-[1px] text-white text-xs">$</div>
							</div>
						</label>

						<label className="w-full col-span-2">
							<span className="text-sm mb-3 block">
								Description
							</span>
							<TextAreaInput
								placeholder="Add Description"
								textAreaClassName="placeholder:text-white/60 h-[190px]"
								onChange={(value) => setDescription(value)}
								divClassName="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						<label className="w-full col-span-2">
							<span className="text-sm mb-3 block">Payment Methods</span>
							<div className="flex gap-6">
								{paymentOptions.map(option => (
									<label key={option.value} className="flex items-center gap-2">
										<input
											type="checkbox"
											value={option.value}
											checked={paymentMethods.includes(option.value)}
											onChange={e => {
												if (e.target.checked) {
													setPaymentMethods([...paymentMethods, option.value])
												} else {
													setPaymentMethods(paymentMethods.filter(v => v !== option.value))
												}
											}}
										/>
										<span>{option.label}</span>
									</label>
								))}
							</div>
						</label>

						<label className="w-full text-white date-input  max-sm:col-span-2">
							<span className="text-sm mb-3 block">Card Release Year</span>
							<div className=" border-[1px] border-gray  rounded-[6px]">
								<input
									inputClassName="placeholder:text-white/60"
									className=" placeholder:text-white/60 bg-transparent text-white p-4 px-4 w-full outline-none focus:outline-none placeholder:text-white resize-none text-xs"
									defaultType="number"
									value={releasedYear}
									onChange={(e) => setReleasedYear(e.currentTarget.value)}
									onBlur={handleReleaseYearChange}
									divClassName="border-[1px] border-gray text-xs rounded-[6px]"
								/>
							</div>
						</label>

						<label className="w-full  max-sm:col-span-2">
							<span className="text-sm mb-3 block">
								Condition
							</span>
							<Dropdown
								options={conditions}
								placeholder="Select Condition"
								onChange={(e) => setCondition(e.target.value)}
								className="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						{/* <label className="w-full col-span-2">
							<span className="text-sm mb-3 block">
								Box Type
							</span>
							<Dropdown
								options={rarities}
								placeholder="Select Box Type"
								onChange={(e) => setRarity(e.target.value)}
								className="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label> */}

						<label className="w-full text-white date-input  max-sm:col-span-2">
							<span className="text-sm mb-3 block">UPC Codes</span>
							<div className=" border-[1px] border-gray  rounded-[6px]">
								<input
									inputClassName="placeholder:text-white/60"
									className="placeholder:text-white/60 bg-transparent text-white p-4 px-4 w-full outline-none focus:outline-none placeholder:text-white resize-none text-xs"
									defaultType="text"
									placeholder="036000291452"
									value={upcCodes}
									onChange={(e) => setUpcCodes(e.currentTarget.value)}
									onBlur={handleUpcCodesChange}
									divClassName="border-[1px] border-gray text-xs rounded-[6px]"
								/>
							</div>
						</label>

						<label className="w-full text-white date-input  max-sm:col-span-2">
							<span className="text-sm mb-3 block">Seller SKUs</span>
							<div className=" border-[1px] border-gray  rounded-[6px]">
								<input
									inputClassName="placeholder:text-white/60"
									className="placeholder:text-white/60 bg-transparent text-white p-4 px-4 w-full outline-none focus:outline-none placeholder:text-white resize-none text-xs"
									defaultType="text"
									placeholder="PKM-CH-HO-001"
									value={sellerSKU}
									onChange={(e) => setSellerSKU(e.currentTarget.value)}
									onBlur={handleSellerSKUChange}
									divClassName="border-[1px] border-gray text-xs rounded-[6px]"
								/>
							</div>
						</label>

						<label className="w-full col-span-2">
							<span className="text-sm mb-3 block">
								Box Type
							</span>
							<Dropdown
								options={boxTypes}
								placeholder="--- None ---"
								allowClear
								onChange={(e) => setBoxType(e.target.value)}
								className="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>

						<label className="w-full col-span-2">
							<span className="text-sm mb-3 block">Available Amount</span>
							<TextInput
								placeholder="Amount"
								inputClassName="placeholder:text-white/60"
								defaultType="number"
								value={quantity}
								min={1}
								onChange={(value) => setQuantity(value)}
								divClassName="border-[1px] border-gray text-xs rounded-[6px]"
							/>
						</label>
					</div>

					<hr className="mt-6 mb-8 border-gray"></hr>

					<button
						className={`flex items-center sm:w-fit w-full justify-center uppercase text-sm text-white px-10 py-3 bg-primary  transition-all ${!activeCreate ? 'opacity-60' : 'hover:text-primary hover:bg-white'}`}
						onClick={onCreateListing}
						disabled={!activeCreate}
					>
						Create Listing
					</button>
				</div>
			</div>
		</div>
	)
}

export default MarketplaceCreateListing
